import { useEffect, useRef, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useLocation,
  useNavigate,
  useRouter,
  type RegisteredRouter,
} from "@tanstack/react-router";
import { meldeAb } from "@/api/auth";
import { meQueryOptions } from "@/api/me";
import { isApiError } from "@/lib/api";
import { PFAD_ANMELDEN } from "@/lib/auth-pfade";
import { meldungVon } from "@/lib/fehler-meldung";
import { alsSearch, ausSearch } from "@/lib/suche-filter";
import type { SearchObjekt } from "@/lib/search-params";
import { Breitenwechsel } from "./Breitenwechsel";

/*
 * Die Hauptnavigation im Kopf — rollenabhängig, aber ohne eigene Rollenlogik.
 *
 * ═══ Der Server entscheidet, die SPA zeigt an ═════════════════════════════
 *
 * Die Punkte kommen 1:1 aus `me.sichten`: Pfad, Titel und Reihenfolge sind
 * fertig, serverseitig nach Rolle gefiltert. Hier steht KEINE Rollen→Pfad-
 * Tabelle, und zwar nicht aus Bequemlichkeit: dieselbe Karte
 * (`internal/auth/navigation.go`) gatet die Endpunkte. Eine zweite, stillere
 * Kopie im Frontend liefe unweigerlich auseinander und bötte dann entweder
 * einen Punkt an, den der Server mit 403 beantwortet, oder verbärge einen
 * erlaubten. Die Begründung ausführlich in `api/me.ts`.
 *
 * Vorher (bis zu diesem Commit) stand `/controlling` fest verdrahtet für alle
 * im Kopf. Das war tragbar, solange es EINE Sicht gab und die Seite selbst
 * 401 ≠ 403 erklärte. Mit jeder weiteren Sicht wuchs ein Punkt, der für
 * manche Rollen ins 403 führt — genau das hört hier auf.
 *
 * ═══ Vier Zustände, vier verschiedene Aussagen ════════════════════════════
 *
 *   Laden       → Platz reservieren, sonst nichts. Kein Text, kein Balken.
 *   200 + Sicht → genau diese Punkte, in genau dieser Reihenfolge.
 *   200 + leer  → angemeldet, keine Sicht freigeschaltet: KEIN Punkt, aber
 *                 auch kein Fehler und kein leerer Rahmen. Der Nutzer ist
 *                 nicht kaputt, er ist unberechtigt — und das erklärt ihm die
 *                 Landung, nicht der Kopf.
 *   401         → Anmelde-Link. Der Normalfall VOR der Anmeldung, kein
 *                 Aussetzer, also kein Fehlerbanner.
 *   sonst       → dezente Störung (praktisch: 5xx oder Netz weg).
 *
 * Der Abmelde-Knopf hängt an GENAU EINER dieser Aussagen: `data !== undefined`,
 * also „der Server hat eine Sitzung bestätigt". Die beiden 200-Zustände
 * bekommen ihn, die drei anderen nicht — bei 401 gibt es nichts zu beenden,
 * beim Laden und bei einer Störung ist unbekannt, ob überhaupt jemand
 * angemeldet ist. Ein Knopf, der in diesem Zweifel angeboten wird, verspricht
 * eine Wirkung, die er nicht kennt.
 *
 * Der Name des Angemeldeten hängt an derselben Aussage, und zwar OHNE eine
 * eigene Bedingung: `data?.name` ist nur dann etwas, wenn `data` etwas ist.
 * Der Bestand zeigt ihn seit jeher (`Header.tsx`), diese SPA bis zu diesem
 * Commit nicht — die Begründungen zur Darstellung stehen bei `NAME_KLASSEN`
 * und an der Fundstelle unten.
 *
 * ═══ Warum der Knopf IM `<nav>` steht — und was daran Abwägung ist ════════
 *
 * Streng genommen ist Abmelden eine Handlung und keine Navigation.
 *
 * **Der Bestand setzt ihn AUSSERHALB** — in `Header.tsx` ist `LogoutButton`
 * ein Geschwister von `HeaderNav`, nicht sein Kind (nachgesehen, nicht
 * angenommen). Hier steht er drinnen, und das ist eine bewusste Abweichung
 * mit einem Grund, der nicht die a11y ist:
 *
 * Für die a11y ist es nämlich fast einerlei. Das `<header>` in `__root.tsx`
 * steht auf oberster Ebene und ist damit selbst eine Landmarke (`banner`);
 * ein Knopf daneben wäre also ebenfalls in einer, nur in einer anderen. Wer
 * hier „sonst wäre er in keiner Landmarke" schriebe, schriebe etwas Falsches.
 *
 * Der Grund ist der „Anmelden"-Knopf zwei Zweige weiter oben: er steht seit
 * jeher IM `<nav>` und ist das Gegenstück desselben Zustands. Die beiden in
 * verschiedene Behälter zu legen, hieße, den Kopf für zwei Fassungen
 * derselben Sache zweimal zu bauen — und die Shell müsste dafür die
 * `me`-Abfrage kennen, die `__root.tsx` ausdrücklich nicht kennen will.
 *
 * Er steht als LETZTES Element: die Tabulatorreihenfolge erreicht damit erst
 * die Sichten und zuletzt das Verlassen — die seltenste Handlung zuletzt.
 *
 * ═══ Warum der Kopf beim Laden nicht springt ══════════════════════════════
 *
 * `min-h-11` steht auf dem `<nav>` SELBST, nicht auf den Punkten. Damit hat
 * der Kopf in allen vier Zuständen dieselbe Höhe: die Wortmarke daneben ist
 * niedriger als 44 px, der Kopf richtet sich also nach der Navigation. Wäre
 * die Höhe erst mit den Punkten da, sackte der Kopf beim ersten Laden um
 * ~16 px zusammen und schöbe den Seiteninhalt mit — der klassische
 * Layout-Shift, und ausgerechnet auf jeder Seite.
 *
 * Aus demselben Grund KEIN Puls-Skelett und kein „Lädt …" im Kopf: die
 * Navigation ist Beiwerk, ein blinkender Balken darüber zieht die
 * Aufmerksamkeit auf die Stelle, an der gerade nichts zu entscheiden ist.
 * `aria-busy` plus `sr-only`-Text bleiben trotzdem (Hausregel für
 * Ladezustände) — ohne sichtbare Animation sind sie hier die EINZIGE
 * Rückmeldung, und für Screenreader die richtige.
 */

/**
 * Die Pfade, die DIESE SPA kennt.
 *
 * Typ und Laufzeitprüfung stammen aus derselben Quelle: `router.routesByPath`
 * ist der Routenbaum, nach vollem Pfad geschlüsselt. Der Typ ist damit exakt
 * die Schlüsselmenge des Objekts, das unten geprüft wird — keine zweite,
 * handgepflegte Liste, die hinter `src/routes/` zurückfällt.
 *
 * Heute löst das zu `"/" | "/controlling" | "/totes-kapital" |
 * "/lieferant/$name"` auf. Steht hier `string`, ist die Registrierung des
 * Routers (`declare module` in `main.tsx`) nicht sichtbar — dann prüft der
 * Compiler die Ziele unten nicht mehr, und der Cast in `alsRouterZiel` wäre
 * ein Blankoscheck.
 */
type BekannterPfad = keyof RegisteredRouter["routesByPath"];

/**
 * Davon die Pfade ohne Parametersegment.
 *
 * Parameter-Routen stehen mit dem PLATZHALTER im Baum (`/lieferant/$name`).
 * Ein Navigationspunkt kann sie nicht bedienen: `Link` will für sie `params`,
 * und was dort einzusetzen wäre, steht in `sichten` nicht. Der Ausschluss ist
 * deshalb Teil des Vertrags dieser Datei und nicht bloß Vorsicht.
 */
type StatischerPfad = Exclude<BekannterPfad, `${string}$${string}`>;

/*
 * Klassen des Bestands-Kopfes: Gelb auf Schwarz trägt, `outline-accent` ist
 * der sichtbare Fokusring. `min-h-11` ist das 44-px-Touch-Target — auf JEDEM
 * klickbaren Element, nicht nur auf dem Container.
 *
 * ═══ Warum die Hervorhebung eine KANTE ist und keine Fläche ══════════════
 *
 * Hier stand `bg-white/10` — ein grau aufgehellter Kasten um den aktiven
 * Punkt. Das ist die Fassung, die dieses Repo beim Port erfunden hat; der
 * Bestand markiert den aktiven Bereich mit einer GELBEN UNTERKANTE
 * (`border-b-2 border-accent` in `HeaderNav.tsx`) und lässt die Fläche
 * schwarz.
 *
 * Der Unterschied ist nicht nur Geschmack, und er ist an zwei Stellen
 * nachlesbar:
 *
 *   Der Kasten ist die EINZIGE aufgehellte Fläche im Kopf und zieht deshalb
 *   den Blick auf sich — auf einen Punkt, auf dem man ohnehin schon steht.
 *   Die Kante sagt dasselbe, ohne eine Fläche einzuführen.
 *
 *   Der Kopf trägt seit diesem Durchgang unten eine 4 px hohe gelbe Kante
 *   (`__root.tsx`). Die Punktmarkierung sitzt in derselben Farbe direkt
 *   darüber und liest sich als ein Reiter an dieser Kante — genau das Bild
 *   des Bestands. Ein grauer Kasten daneben wäre ein zweiter,
 *   widersprechender Markierungskanal.
 *
 * `border-b-transparent` und NICHT `border-transparent`: der Abmelde-Knopf
 * hängt an denselben Klassen und bekommt links eine Trennlinie
 * (`ABMELDEN_TRENNER`). Ein allseitig transparenter Rahmen färbte sie mit
 * ein und die Linie verschwände — sichtbar bliebe nur ihre Breite.
 *
 * ═══ WARUM die Aktiv-Kante als `data-[status=active]` hier steht ════════
 *
 * Weil die naheliegende Fassung GEMESSEN nicht funktioniert hat. Sie lautete:
 * `border-b-transparent` in dieser Konstante, `border-b-accent` in
 * `PUNKT_AKTIV`, vom Router als zweite Klasse angehängt. Am laufenden Dienst
 * nachgesehen, aktiver Punkt „Verkaufssuche":
 *
 *     getComputedStyle(punkt).borderBottom  →  2px solid rgba(0, 0, 0, 0)
 *
 * **Durchsichtig.** Tailwind entscheidet bei zwei Klassen derselben
 * Eigenschaft nach der Reihenfolge im ERZEUGTEN CSS und nicht nach der im
 * `class`-Attribut; `border-b-transparent` steht dort hinter
 * `border-b-accent` und gewinnt. Genau die Falle, die
 * `button-styles.test.ts` bei den Varianten benennt — nur eben eine Ebene
 * höher, an zwei Klassen, die keine Zusage sind.
 *
 * Ein Variantenselektor hat dagegen eine höhere Spezifität
 * (`.…\:border-b-accent[data-status="active"]` ist Klasse + Attribut) und
 * gewinnt unabhängig von der Reihenfolge — dasselbe, weshalb `hover:` schon
 * vorher funktioniert hätte. `data-status="active"` setzt `Link` selbst.
 *
 * Damit ist die Markierung EIN Kanal und nicht zwei: sie steht vollständig
 * hier, `PUNKT_AKTIV` trägt nur noch die Tinte. Was die Zeichenkette hier
 * sagt, ist aber nicht, was der Browser tut — die Prüfung darauf steht
 * deshalb in `kopf-mobil.spec.ts` und misst die gerechnete Farbe.
 */
const PUNKT_KLASSEN =
  "inline-flex min-h-11 items-center border-b-2 border-b-transparent px-3 text-sm font-normal text-on-brand/80 transition-colors hover:border-b-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent data-[status=active]:border-b-accent data-[status=active]:font-medium";

/*
 * `activeProps` statt eigener Zustandslogik: der Router weiß, wo er steht.
 * `aria-current="page"` (und `data-status="active"`) setzt `Link` selbst —
 * hier steht nur das Aussehen. Die KANTE steht nicht mehr hier, sondern als
 * `data-[status=active]`-Variante in `PUNKT_KLASSEN`; die Begründung dort.
 */
const PUNKT_AKTIV = { className: "text-on-brand" };

/**
 * Der aktive Punkt hängt am PFAD, nicht an der Adresse.
 *
 * ═══ Warum das seit dem Filter-Durchreichen ausdrücklich dastehen MUSS ═══
 *
 * `Link` vergleicht von sich aus AUCH die Search-Params: `includeSearch`
 * steht in TanStack Router auf `true`, und der Vergleich ist
 * `deepEqual(ort.search, link.search, { partial: true })`. Solange die Punkte
 * gar keine Search-Params trugen, war dieser Vergleich trivial wahr — ein
 * leeres Objekt ist Teilmenge von allem. Mit den Filtern daran ist er es
 * nicht mehr, und er wäre an einer Stelle sogar FALSCH:
 *
 *     Ort   /verkaufssuche?q=Fliese&loc=Wismar
 *     ort.search    { q: "Fliese", loc: "Wismar"   }   ← roh geparst
 *     link.search   { loc: ["Wismar"], q: "Fliese" }   ← durch `ausSearch`
 *
 * `deepEqual("Wismar", ["Wismar"])` ist `false`. Der Punkt, auf dem man
 * STEHT, verlöre seine gelbe Kante — und zwar nur dann, wenn genau ein Wert
 * für einen Listen-Parameter gesetzt ist. Gemessen an einem Router mit den
 * Serialisierern dieser App, nicht überlegt.
 *
 * `includeSearch: false` ist dabei nicht die Reparatur eines Vergleichs,
 * sondern die richtige AUSSAGE: ein Navigationspunkt markiert einen BEREICH.
 * Wer die Verkaufssuche filtert, ist weiterhin in der Verkaufssuche. Der
 * Bestand entscheidet es genauso — `isNavActive(pathname, item)` in
 * `nav-items.ts` sieht die Query gar nicht an.
 */
const PUNKT_AKTIV_OPTIONEN = { includeSearch: false };

/*
 * Der Abmelde-Knopf trägt DIESELBEN Klassen wie ein Punkt — bewusst, und nicht
 * aus Bequemlichkeit: er sitzt in derselben Zeile, und eine eigene Farbgebung
 * machte aus einer Nebenhandlung die auffälligste Fläche im Kopf. Der
 * „Anmelden"-Knopf darüber ist der Gegenfall: dort ist es die EINZIGE Handlung,
 * die gerade etwas bringt, deshalb Gelb.
 *
 * ═══ Warum nicht `KNOPF_BASIS` aus `lib/button-styles.ts` ════════════════
 *
 * Gemessen, nicht angenommen: `KNOPF_BASIS` trägt `focus-visible:outline-brand-black`
 * — ein SCHWARZER Fokusring, und dieser Kopf ist `bg-brand-black`. Der Ring
 * wäre unsichtbar. Reparieren ließe sich das nur an der Variante, und genau
 * das verbietet `button-styles.test.ts` mit Absicht („Die Varianten treten der
 * Basis nicht in die Zusagen"). Die Basis ist für den Inhaltsbereich gebaut;
 * der Kopf hat seine eigene Palette, und die steht seit jeher hier.
 *
 * Was `KNOPF_BASIS` an ZUSAGEN trägt, steht deshalb hier ausdrücklich mit
 * drin: `min-h-11` (44-px-Touch-Target) kommt aus `PUNKT_KLASSEN`, der
 * sichtbare `disabled`-Zustand aus der Zeile darunter. Der Fokusring ist
 * `outline-accent` — Gelb auf Schwarz, dieselbe Paarung wie bei den Punkten.
 */
const ABMELDEN_KLASSEN = `${PUNKT_KLASSEN} disabled:cursor-not-allowed disabled:opacity-50`;

/*
 * Die Trennlinie zwischen Sichten und Abmelden.
 *
 * Nur, wenn links davon überhaupt ein Punkt steht: im Zustand „angemeldet,
 * keine Sicht" wäre sie ein Strich neben dem Nichts und sähe wie ein
 * Darstellungsfehler aus. Ohne sie läse sich „Controlling · Totes Kapital ·
 * Abmelden" dagegen wie drei gleichrangige Sichten.
 *
 * Sie hängt weiterhin an `punkte.length > 0` und AUSDRÜCKLICH nicht daran, ob
 * daneben ein Name steht. Das ist keine Nachlässigkeit, sondern dieselbe
 * Aussage wie oben: der Strich trennt die HANDLUNG von den Sichten. Ein Name
 * ist keine Sicht — wo keine Punkte sind, gibt es auch mit Namen nichts zu
 * trennen.
 */
const ABMELDEN_TRENNER = "ml-2 border-l border-l-white/20";

/*
 * Der Name des Angemeldeten.
 *
 * Er ist TEXT und keine Handlung: kein `min-h-11` (die 44-px-Hausregel gilt
 * klickbaren Elementen, und was nicht klickbar ist, braucht keine Zielfläche),
 * keine Hover-Fläche, kein Fokusring — es gibt nichts zu fokussieren. Eine
 * Fläche, die auf Zeigen reagiert, verspricht einen Klick, den es nicht gibt.
 *
 * Auf die Höhe der Nachbarn wirkt er dadurch nicht: das `<nav>` ist
 * `items-center` und nicht `items-stretch`, jedes Kind behält seine eigene
 * Höhe, und die 44 px des `<nav>` selbst stehen ohnehin fest. Ein ~20 px hoher
 * Textkasten daneben verändert daran nichts.
 *
 * ═══ Was auf schmalem Gerät passiert — und warum ohne Umbruchpunkt ════════
 *
 * Der Bestand blendet den Namen unter `lg` aus (`hidden lg:inline` in
 * `Header.tsx`) und zeigt ihn dort stattdessen im Schubfach der
 * Mobil-Navigation (`MobileMenu.tsx`, Fußzeile — mit `truncate` und `title`).
 * Die erste Hälfte ohne die zweite wäre hier eine Verschlechterung: diese SPA
 * hat kein Schubfach. Der Name wäre auf dem Telefon für ALLE weg, auch für den
 * Screenreader, denn `hidden` ist `display:none` und nimmt den Knoten aus dem
 * Baum. Der Bestand kann sich das leisten, weil er den Namen zwei Zeilen
 * weiter wieder einblendet; wir können es nicht.
 *
 * `truncate` erledigt dieselbe Aufgabe ohne Umbruchpunkt, und zwar über die
 * Physik der Zeile: `truncate` setzt `overflow: hidden`, und für ein Flex-Kind
 * mit `overflow != visible` ist die automatische Mindestbreite 0 statt der
 * Inhaltsbreite. Damit ist der Name das EINZIGE Kind dieses `<nav>`, das
 * überhaupt schrumpfen kann — Punkte und Knopf sind sichtbar überlaufend und
 * behalten ihre Inhaltsbreite. Wird die Zeile eng, gibt der Name nach, bis er
 * verschwindet; ist Platz da, steht er vollständig. Im Baum bleibt er in JEDER
 * Breite, der Screenreader verliert ihn nie.
 *
 * Der Kopf hatte bis Posten 57 keine eigene Mobil-Auslegung: die Zeile in
 * `__root.tsx` war ein `flex` OHNE `flex-wrap`, und schon ohne Namen kamen
 * Wortmarke, Untertitel, zwei Punkte und der Abmelde-Knopf über die Breite
 * eines Telefons. Dieser Absatz rechnete das „grob aus den Mindestbreiten,
 * nicht gemessen" — inzwischen IST es gemessen, im Browser auf 320 px, und
 * die Schätzung stimmte der Richtung nach:
 *
 *     ohne Sitzung   338 px   innerster Knoten: a „Anmelden"
 *     eine Sicht     406 px   innerster Knoten: button „Abmelden"
 *     Administrator  827 px   fünf Punkte, Name und Abmelden
 *
 * Seither bricht die Zeile um (`flex-wrap` dort, `flex-wrap` an diesem
 * `<nav>`), und `e2e/specs/kopf-mobil.spec.ts` hält, dass das Dokument auf
 * 320 px nicht mehr seitwärts scrollt.
 *
 * Für den Namen ändert das NICHTS an der Abwägung darunter. Er gibt weiterhin
 * als Erstes nach, und das ist jetzt sogar der bessere Zustand: umbrechen
 * kostet Höhe, und eine Zeile, die der Name allein füllt, wäre die
 * überflüssigste davon.
 *
 * Und noch ein Grund: Breiten-Varianten gibt es in dieser SPA reichlich
 * (`sm:grid-cols-2` und Verwandte), aber sie legen um, sie nehmen nichts weg.
 * Was in `src/` überhaupt nach Breite AUSBLENDET, steht an genau einer Stelle
 * und wird dort auch begründet und geprüft — siehe `components/
 * Breitenwechsel.tsx`, Abschnitt „Der Halter der Aussage". Hier wird die
 * Aussage bewusst nicht wiederholt: sie liefe sonst auseinander.
 *
 * Für den Namen ändert das nichts, im Gegenteil — der Unterschied ist genau
 * das Argument von oben. Ein Umschalter blendet eine Hälfte aus und stellt
 * die andere daneben; ein `hidden lg:` am Namen nähme ihn ersatzlos weg,
 * weil diese SPA kein Schubfach hat. Das eine ist ein Wechsel, das andere
 * ein Verlust.
 *
 * `max-w-48` (12 rem — dieselbe Grenze wie im Bestand; `--spacing` ist
 * 0.25 rem, im gebauten CSS nachgesehen) deckt den anderen Fall:
 * ein sehr langer Name soll den Kopf nicht auseinanderziehen. Die vollständige
 * Fassung steht dann im `title`; dasselbe Paar steht in `ArchivSeite.tsx`
 * (`max-w-56 truncate` + `title`) und ist hier keine neue Erfindung.
 *
 * Dass `max-w-*` und `truncate` an einem `<span>` überhaupt greifen, liegt am
 * Flex-Kontext: ein Flex-Kind wird blockifiziert, aus `display:inline` wird
 * `block`. An einem echten Inline-Kasten wären beide wirkungslos — auch das
 * ein Grund, den Namen NICHT in ein weiteres Element einzuwickeln.
 *
 * Blasser und ohne `font-medium`, damit er nicht als dritte Sicht gelesen
 * wird: `/70` ist dieselbe Stufe wie „Altwaren-Suche" neben der Wortmarke in
 * `__root.tsx` — die Stufe für Beiwerk auf Schwarz.
 */
const NAME_KLASSEN = "max-w-48 truncate px-2 text-sm text-on-brand/70";

/**
 * Übersetzt einen Serverpfad in ein Ziel, das der Router bauen kann — oder in
 * `null`, wenn diese SPA den Pfad nicht kennt.
 *
 * ═══ Warum ein unbekannter Punkt WEGFÄLLT ═════════════════════════════════
 *
 * Der Server kann Sichten anbieten, die es hier nicht gibt. Seit dem
 * siebzehnten Durchgang ist das KEINE mehr: `auth.NavItems` führt
 * `/verkaufssuche`, `/suche`, `/controlling`, `/totes-kapital` und `/admin`,
 * und alle fünf stehen im Routenbaum dieser SPA. Die Aufzählung, die hier
 * stand, ist damit leer — sie hat sich selbst überlebt, wie es der Absatz
 * unten vorhergesagt hat.
 *
 * Die Mechanik bleibt trotzdem, und zwar nicht aus Vorsicht: sie ist der
 * Grund, warum diese Datei beim Wachsen des Servers nichts zu pflegen hat.
 * Für einen Pfad ohne Route gibt es drei Möglichkeiten, und zwei davon sind
 * schlechter:
 *
 *   1. Router-`Link` darauf. Der Router findet nichts und zeigt seine
 *      Not-Found-Seite — ein Punkt, der in eine Fehlseite führt.
 *   2. Voll-Navigation (`<a href>`). Das Go-Binary hängt die SPA als
 *      Not-Found-Route ein (`RegisterSPA`), lädt also die SPA neu — und landet
 *      in derselben Not-Found-Seite, nur nach einem kompletten Reload.
 *   3. Weglassen.
 *
 * Es bleibt (3): ein Navigationspunkt, der ins Nichts führt, ist schlechter
 * als keiner. Der Nutzer verliert dadurch nichts, was er hätte haben können —
 * die Sicht ist nicht gebaut, nicht gesperrt. Und der Zugang bleibt heil: der
 * Server gatet weiter, das Weglassen ist reine Anzeige.
 *
 * Sobald `/suche` als Route existiert, erscheint der Punkt ohne Änderung an
 * dieser Datei. Genau deshalb steht die Prüfung am Routenbaum und nicht an
 * einer Liste hier.
 *
 * ═══ Und das ist inzwischen eingetreten, nicht bloß behauptet ════════════
 *
 * Als dieser Kommentar geschrieben wurde, nannte er `/suche`, `/verkaufssuche`
 * UND die Administration als fehlend. Alle drei sind gebaut worden, und der
 * Punkt erschien jedes Mal ohne eine Zeile Änderung hier. Der Satz oben
 * schrumpfte erst auf eine Sicht zusammen — und ist im siebzehnten Durchgang
 * leer geworden, genau wie er es an dieser Stelle vorhergesagt hat.
 *
 * Dass die Vorhersage eintraf, ist der eigentliche Beleg: die Prüfung unten
 * hat drei Sichten kommen sehen, ohne dass jemand sie ihr genannt hätte.
 *
 * Die Lehre gilt der Datei selbst: eine Aufzählung in Prosa veraltet, ein
 * Blick in den Routenbaum nicht. Wer hier eine Sicht nachträgt, pflegt eine
 * zweite Liste — die Prüfung darunter braucht sie nicht.
 */
/**
 * Die Filter-Parameter der aktuellen Adresse — das, was ein Sichtwechsel
 * MITNEHMEN muss.
 *
 * ═══ Der Defekt, gegen den das steht ═════════════════════════════════════
 *
 * TanStack Router nimmt Search-Params beim Navigieren NICHT von selbst mit.
 * Gemessen an einem Router mit den Routen dieser App:
 *
 *     /verkaufssuche?q=Fliese&loc=Wismar&page=3
 *       navigate({ to: "/suche" })        →  /suche
 *       navigate({ to: "/controlling" })  →  /controlling
 *
 * Wer eine Suche zusammengeklickt hatte und die Sicht wechselte, klickte sie
 * danach neu. Das gilt für JEDEN Wechsel über den Kopf, auch für den zurück.
 *
 * ═══ Warum die Params durch `ausSearch` und `alsSearch` laufen ═══════════
 *
 * Nicht, um sie zu verändern — um sie auf die NEUNZEHN einzugrenzen, die es
 * gibt, und in die Form zu bringen, die `stringifySearch` erwartet:
 *
 *   1. **`von` bleibt draussen.** Es steht auf `/artikel/$id` in der Adresse
 *      und sagt „hierher kam der Lesende". Ein Navigationspunkt ist kein
 *      Rückweg; er trüge sonst eine Herkunftsbehauptung in eine Sicht, die
 *      man gerade betritt. `ausSearch` kennt den Parameter nicht und lässt
 *      ihn damit von selbst fallen — dieselbe Grenze, die `validateSearch`
 *      an den beiden Suchrouten zieht.
 *   2. **Die Listen werden vereinheitlicht.** `parseSearch` liefert ein
 *      einzelnes Vorkommen als String und mehrere als Array (`loc=A` →
 *      `"A"`); `ausSearch` macht daraus immer eine Liste. Ohne das schriebe
 *      der Link denselben Parameter mal so und mal so.
 *   3. **Leere Einzelwerte fallen weg, leere Listen NICHT.** Genau die
 *      Unterscheidung, die `alsSearch` trifft: `q=` sagt nichts, `loc=` heisst
 *      „alle Standorte". Die Begründung steht in `lib/suche-filter.ts`.
 *
 * ═══ Warum das an JEDEM Punkt hängt, auch an `/admin` ════════════════════
 *
 * Weil „erhalten bleiben" sonst von der Reihenfolge der Klicks abhinge. Ein
 * Punkt, der die Params fallen lässt, ist eine Falltür: wer über ihn geht und
 * zurückkommt, hat den Filter verloren, und der Weg dorthin ist nicht
 * ersichtlich. Der Bestand hängt sie ebenfalls an ALLE Punkte
 * (`navHrefWithQuery` in `nav-items.ts`).
 *
 * Für die Sichten, die den Filter nicht lesen, ist er dabei folgenlos:
 * `/controlling` und `/totes-kapital` haben kein `validateSearch`, die
 * Parameter stehen dort in der Adresse und sonst nirgends. Gemessen — sie
 * überleben eine Route ohne `validateSearch` unverändert.
 */
function filterAusOrt(roh: Record<string, unknown>): SearchObjekt {
  return alsSearch(ausSearch(roh));
}

function alsRouterZiel(routenNachPfad: object, pfad: string): StatischerPfad | null {
  // Erst der Platzhalter, dann der Baum: `/lieferant/$name` IST ein Schlüssel
  // in `routesByPath`, und ohne diese Zeile wäre der Cast unten unwahr.
  if (pfad.includes("$")) return null;
  if (!Object.hasOwn(routenNachPfad, pfad)) return null;
  return pfad as StatischerPfad;
}

/**
 * Der Störungstext. Mit Status, wenn es einen gibt: „HTTP 503" ist das, was
 * ein Nutzer am Telefon weitergeben kann.
 */
function stoerungsText(error: unknown): string {
  if (isApiError(error) && !error.isNetworkError) {
    return `Navigation nicht geladen (HTTP ${String(error.status)})`;
  }
  return "Navigation nicht geladen";
}

export function Navigation(): ReactElement {
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, error, isPending } = useQuery(meQueryOptions);
  const [mobilOffen, setMobilOffen] = useState(false);
  const menueknopf = useRef<HTMLButtonElement>(null);
  const mobildialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (mobilOffen && mobildialog.current !== null && !mobildialog.current.open) {
      mobildialog.current.showModal();
    }
    const dialog = mobildialog.current;
    const hintergrund = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      dialog?.close();
      setMobilOffen(false);
      requestAnimationFrame(() => menueknopf.current?.focus());
    };
    dialog?.addEventListener("click", hintergrund);
    return () => {
      dialog?.removeEventListener("click", hintergrund);
    };
  }, [mobilOffen]);

  const abmelden = useMutation({
    mutationFn: meldeAb,
    onSuccess: () => {
      /*
       * Vor dem Login-Wechsel den Sitzungszustand entfernen: die Login-Seite
       * würde bei einer alten erfolgreichen `me`-Antwort direkt weiterleiten.
       *
       * `clear()` und nicht `invalidateQueries(["me"])`: im Cache liegen die
       * Controlling-Aggregate, die Lieferantenlisten und die
       * Schlüsseltabelle des Abgemeldeten. Wer sich danach an demselben
       * Browser anmeldet, darf davon nichts sehen — auch nicht für den einen
       * Frame, bis die erste Antwort da ist. Das Cookie ist `HttpOnly`, der
       * Server hat es gelöscht; alles, was von der Sitzung noch übrig ist,
       * liegt genau hier.
       *
       * Der laufende `me`-Beobachter im Kopf holt sich danach von selbst eine
       * frische Antwort — die dann 401 ist und die Microsoft-Anmeldung in
       * der Login-Karte freigibt. Im Login-Kopf gibt es keinen zweiten Knopf.
       * Das ist erwünscht: es ist die Bestätigung, dass die Sitzung weg ist,
       * und sie kommt vom Server statt aus einer Annahme dieser Datei.
       */
      queryClient.clear();
      void navigate({ to: "/login" });
    },
  });

  /*
   * 401 ist KEINE Störung, sondern der Zustand „noch nicht angemeldet" — der
   * Normalfall beim ersten Aufruf. Alles andere, was schiefgeht, ist eine:
   * 5xx aus dem Backend oder ein Netzfehler (`status === 0`). Ein 403 kann
   * dieser Endpunkt nicht liefern (Spec: 200 oder 401); käme er doch, wäre er
   * ein Programmfehler und gehört zu den Störungen.
   */
  const nichtAngemeldet = isApiError(error) && error.status === 401;
  const stoerung = error !== null && !nichtAngemeldet;

  /*
   * Der einzige Zustand, in dem es etwas zu beenden GIBT: der Server hat eine
   * Sitzung bestätigt. Bewusst an `data` und nicht an `!nichtAngemeldet` —
   * das wäre beim Laden und bei einer Störung ebenfalls wahr, und dort ist
   * schlicht unbekannt, ob jemand angemeldet ist. Ein Abmelde-Knopf im
   * Zweifel verspricht eine Wirkung, die er nicht kennt.
   *
   * Er steht auch dann, wenn `sichten` leer ist — GERADE dann: wer angemeldet
   * ist und keine Sicht freigeschaltet bekommen hat, hat sonst überhaupt
   * keine Handlung mehr (im Bestand ist das die `KeineSichtSeite`, deren
   * einziger Knopf das Abmelden ist).
   */
  const angemeldet = data !== undefined;

  /*
   * `sichten` ist `array | null` und zugleich `required` — `null` heißt
   * dasselbe wie `[]`: keine Sicht freigeschaltet. `flatMap` filtert und
   * bildet in einem Durchgang ab und BEHÄLT DIE REIHENFOLGE des Servers; sie
   * ist bedeutungstragend (dieselbe Liste bestimmt serverseitig die Landung).
   */
  /*
   * Der Anzeigename — und warum er an DERSELBEN Bedingung hängt wie der
   * Abmelde-Knopf, ohne sie zu wiederholen.
   *
   * `data?.name` ist genau dann etwas, wenn `data` etwas ist. Die
   * Optional-Kette IST die Bedingung `angemeldet` und keine zweite Kopie
   * davon, die auseinanderlaufen könnte. Damit gilt für den Namen automatisch
   * die Abwägung, die oben für den Knopf ausgeschrieben steht: beim Laden und
   * bei einer Störung ist unbekannt, ob überhaupt jemand angemeldet ist — und
   * ein Name, der erscheint und wieder verschwindet, ist schlechter als einer,
   * der eine Antwort später kommt. Bei 401 gibt es keinen.
   *
   * Er kommt außerdem im SELBEN Bild wie Punkte und Knopf. Er kann deshalb gar
   * keinen zweiten Umbruch auslösen: es gibt nur einen Moment, in dem sich der
   * Kopf füllt, und der ist der bestehende.
   *
   * ═══ Der Vertrag sagt: der Name KANN fehlen ══════════════════════════════
   *
   * Nachgesehen und nicht vermutet. `MeKoerper` in `docs/openapi.json` führt
   * `name` NICHT unter `required` („Anzeigename; fehlt, wenn keiner hinterlegt
   * ist"), und `schema.d.ts` bildet das als `name?: string` ab. `null` steht
   * dort nicht, und es kann auch nicht kommen: das Go-Feld ist ein `string`
   * mit `omitempty` (`internal/api/auth.go`) — ein leerer Name fällt schon am
   * Draht weg, statt als `""` anzukommen.
   *
   * Der `?? ""` fasst beide Formen zu einer zusammen. Fehlt der Name, entsteht
   * gar kein Knoten — und damit auch keine leere Lücke im Kopf, denn das
   * `gap-1` des `<nav>` wirkt nur zwischen Kindern, die es gibt. Ein leerer,
   * aber vorhandener Span hinterließe genau diese Lücke.
   *
   * KEIN `trim()`: der Server schneidet den Namen an jeder Stelle zu, an der
   * er entsteht — `auth/entra/entra.go` (Anmeldung), `api/nutzer.go`
   * (Pflege), `store/users.go` (Anlage und Abgleich). Ein zweiter Zuschnitt
   * hier wäre eine Regel, die nie greift, und kein Test könnte sie beobachten.
   */
  const angezeigterName = data?.name ?? "";

  /*
   * Die Adresse, an der der Lesende gerade steht — roh, wie `parseSearch` sie
   * geliefert hat. NICHT der geprüfte Filter einer Route: dieser Kopf steht
   * über allen Sichten, auch über denen ohne `validateSearch`, und dort gäbe
   * es keinen.
   */
  const ortSearch = useLocation({ select: (ort) => ort.search });
  const ortPfad = useLocation({ select: (ort) => ort.pathname });
  const einstieg = ortPfad === "/login" || ortPfad === "/";
  const filterSearch = filterAusOrt(ortSearch);

  const punkte = (data?.sichten ?? []).flatMap((sicht) => {
    const ziel = alsRouterZiel(router.routesByPath, sicht.pfad);
    return ziel === null ? [] : [{ ziel, titel: sicht.titel }];
  });

  // Der anonyme Einstieg hat seine eigene Anmeldung. Auch mobil kein leeres
  // Navigationsmenü anbieten; die Höhe des gemeinsamen Kopfes bleibt stabil.
  if (einstieg && !angemeldet) return <div className="ml-auto min-h-11" />;

  const schliessen = () => {
    setMobilOffen(false);
    requestAnimationFrame(() => menueknopf.current?.focus());
  };

  const navigationsInhalt = (
    <>
      {isPending && <span className="sr-only">Navigation wird geladen …</span>}

      {stoerung && (
        /*
         * `role="status"` und NICHT `role="alert"`: assertiv unterbrechen darf
         * etwas, das den Nutzer aufhält. Die Navigation hält nichts auf — die
         * Seite darunter lädt ihre Daten selbst und erklärt ihren eigenen
         * Zustand. Und deshalb hier auch kein `FehlerBanner` aus
         * `StatusKarte.tsx`: der ist eine Karte für den Inhaltsbereich, im Kopf
         * wäre er ein Klotz, der die Störung größer macht als sie ist.
         *
         * Farbe aus der Kopf-Palette, nicht aus den Warn-Tokens: die sind auf
         * `warning-bg` (hell) abgestimmt, ihr Kontrast auf `brand-black` ist
         * ungeprüft. Gelb auf Schwarz ist im Kopf die bewährte Paarung — es ist
         * dieselbe wie in der Wortmarke.
         */
        <span
          role="status"
          className={
            mobilOffen
              ? "inline-flex min-h-11 items-center px-3 text-sm font-medium text-warning-strong"
              : "inline-flex min-h-11 items-center px-3 text-sm font-medium text-accent"
          }
        >
          {stoerungsText(error)}
        </span>
      )}

      {nichtAngemeldet && !einstieg && (
        /*
         * Normaler `<a>`, KEIN Router-`Link`: `/auth/login` ist eine Route des
         * Go-Servers (`internal/api/auth.go`, `PathAuthLogin`), nicht der SPA.
         * Ein Router-Link navigierte clientseitig, fände im Routenbaum nichts
         * und erreichte die Anmeldung nie. Dieselbe Begründung steht an
         * `ZugangFehlt` in `Tabelle.tsx` — hier ist sie dieselbe Sache an der
         * zweiten Stelle, nicht eine zweite Regel.
         *
         * Betont (Gelb) und nicht wie ein Punkt gesetzt: es ist die einzige
         * Handlung, die im Kopf gerade etwas bringt.
         */
        <a
          href={PFAD_ANMELDEN}
          className="inline-flex min-h-11 items-center rounded-md bg-accent px-4 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-brand"
        >
          Anmelden
        </a>
      )}

      {punkte.map(({ ziel, titel }) => (
        <Link
          key={ziel}
          to={ziel}
          /*
           * Der Filter wandert mit — die Begründung steht bei `filterAusOrt`.
           * Er wird bei JEDEM Rendern neu gebaut und nicht gemerkt: er hängt
           * an der Adresse, und die ist der Zustand. Ein `useMemo` darüber
           * spielte einen Cache gegen eine Funktion, die drei Listen abläuft.
           */
          search={filterSearch}
          activeOptions={PUNKT_AKTIV_OPTIONEN}
          className={
            mobilOffen
              ? "inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-strong hover:bg-surface-soft focus-visible:outline-2 focus-visible:outline-brand-black"
              : PUNKT_KLASSEN}
          activeProps={
            mobilOffen ? { className: "bg-accent text-on-accent" } : PUNKT_AKTIV}
          onClick={() => {
            if (mobilOffen) schliessen();
          }}
        >
          {titel}
        </Link>
      ))}

      {angezeigterName !== "" && (
        /*
         * ═══ Warum der Name HIER steht ═══════════════════════════════════
         *
         * Reihenfolge wie im Bestand (`Header.tsx`: HeaderNav, userName,
         * LogoutButton): Sichten, Name, Abmelden. Zwischen Name und Knopf
         * steht nur noch die Fehlermeldung des Abmeldens — die gehört zum
         * Knopf und soll ihm am nächsten sein.
         *
         * Der Name steht damit links vom Trenner, also auf der Seite der
         * Punkte. Dass er trotzdem nicht als dritte Sicht gelesen wird, trägt
         * die Schrift und nicht die Anordnung: die Punkte sind heller (`/80`),
         * die aktive Sicht zusätzlich `font-medium`; der Name ist normal
         * gesetzt, blasser und reagiert auf nichts.
         *
         * Kein `role`, kein `aria-live`, kein Link: das hier ist eine Angabe,
         * keine Meldung und keine Handlung. Er erscheint einmal mit dem Rest
         * des Kopfes; etwas, das sich meldet, wäre eine Unterbrechung ohne
         * Anlass.
         */
        <span className={
            mobilOffen
              ? "mt-3 max-w-full truncate px-3 text-sm text-muted"
              : NAME_KLASSEN} title={angezeigterName}>
          {/*
           * Für sich genommen ist ein Name im Kopf zweideutig — wessen Name?
           * Sichtbar beantwortet das die Stelle (neben dem Abmelden), für
           * einen Screenreader beantwortet es dieser Vorsatz; der Bestand
           * lässt ihn unbeantwortet. Er steht IM Span und nicht daneben, damit
           * beide dasselbe Schicksal haben — verschwindet der Name, ist auch
           * die Einleitung weg statt allein stehen zu bleiben.
           *
           * Als Ausdruck geschrieben und nicht als nackter Text, weil das
           * abschließende Leerzeichen bedeutungstragend ist: es trennt
           * Einleitung und Namen in der Vorlesung. JSX würde es beim
           * Umbrechen der Zeile wegwerfen.
           */}
          <span className="sr-only">{"Angemeldet als "}</span>
          {angezeigterName}
        </span>
      )}

      {abmelden.isError && (
        /*
         * `role="alert"` und NICHT `role="status"` wie bei der Störung oben —
         * der Unterschied ist inhaltlich: eine nicht geladene Navigation hält
         * niemanden auf, ein gescheitertes Abmelden schon. Der Nutzer hat
         * geklickt, hält sich für abgemeldet und IST es nicht; das Cookie ist
         * `HttpOnly` und lebt weiter. Genau das muss der Satz sagen, und
         * assertiv.
         *
         * Er nennt beides: die Folge („noch angemeldet") und die Ursache aus
         * `meldungVon`. Ohne die Folge wäre nur zu lesen, dass etwas
         * schiefging — nicht, was das für den Zugang bedeutet.
         *
         * `meldungVon` OHNE `StatusTexte`: `/auth/logout` deklariert in der
         * Spec keine einzige Fehlerantwort (nur 204), ein Satz zu irgendeinem
         * Code wäre also wirkungslos. Übrig bleiben Netzausfall (Status 0 →
         * `NETZ_MELDUNG`) und was ein Proxy davor erfindet.
         */
        <span
          role="alert"
          className={
            mobilOffen
              ? "inline-flex min-h-11 max-w-sm items-center px-3 text-sm font-medium break-words text-warning-strong"
              : "inline-flex min-h-11 max-w-sm items-center px-3 text-sm font-medium break-words text-accent"
          }
        >
          Abmelden fehlgeschlagen — Sie sind weiterhin angemeldet.{" "}
          {meldungVon(abmelden.error)}
        </span>
      )}

      {angemeldet && (
        <button
          type="button"
          onClick={() => {
            abmelden.mutate();
            if (mobilOffen) schliessen();
          }}
          disabled={abmelden.isPending}
          className={
            mobilOffen
              ? "inline-flex min-h-11 items-center rounded-lg px-3 text-left text-sm text-strong hover:bg-surface-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-black disabled:cursor-not-allowed disabled:opacity-50"
              : punkte.length > 0 ? `${ABMELDEN_KLASSEN} ${ABMELDEN_TRENNER}` : ABMELDEN_KLASSEN}
        >
          {/*
           * Beschriftung wechselt mit dem Lauf — Hausmuster aus
           * `ApiKeysSeite` („Erzeuge …") und `UploadSeite`. Der Knopf ist
           * dabei `disabled`, ein zweiter Klick kann also nicht ins Leere
           * gehen.
           */}
          {abmelden.isPending ? "Melde ab …" : "Abmelden"}
        </button>
      )}
    </>
  );

  return (
    <div className="ml-auto min-w-0">
      <Breitenwechsel
        umbruch="md"
        schmal={
          <button
            ref={menueknopf}
            type="button"
            aria-label="Navigation öffnen"
            aria-expanded={mobilOffen}
            onClick={() => setMobilOffen(true)}
            className="ml-auto inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-white/25 text-on-brand transition-colors hover:bg-surface-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              className="size-6"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        }
        breit={
          <nav
            aria-label="Hauptnavigation"
            aria-busy={isPending || undefined}
            className="ml-auto flex min-h-11 items-center gap-1"
          >
            {navigationsInhalt}
          </nav>
        }
      />

      {mobilOffen && (
        <dialog
          ref={mobildialog}
          aria-label="Navigation"
          className="fixed inset-0 z-50 m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-black/55"
          onCancel={(event) => {
            event.preventDefault();
            schliessen();
          }}
        >
          <section
            className="mr-auto flex h-full max-h-full w-[min(22rem,calc(100%_-_3rem))] flex-col overflow-auto bg-background p-4 text-strong shadow-2xl">
            <header className="mb-3 flex items-center justify-between border-b border-line pb-3">
              <span className="text-sm font-semibold uppercase tracking-wide">Navigation</span>
              <button
                type="button"
                aria-label="Navigation schließen"
                onClick={schliessen}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-2xl leading-none transition-colors hover:bg-surface-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                ×
              </button>
            </header>
            <nav
              aria-label="Mobile Hauptnavigation"
              aria-busy={isPending || undefined}
              className="flex min-h-11 flex-col items-stretch gap-1"
            >
              {navigationsInhalt}
            </nav>
          </section>
        </dialog>
      )}
    </div>
  );
}
